import { styled } from "goober";
import { useState } from "react";
import { ClickButton } from "../ClickButton";
import { PanelToggleIcon } from "../PanelToggleIcon";
import { ShortcutHint } from "../ShortcutHint";
import { Tooltip } from "../Tooltip";

const FloatingToggle = styled("div")`
  display: none;
  &[data-expanded="false"] {
    display: flex;
  }
`;

// Mirrors the animated sidebar toggle (services/ui/components/Navigation.tsx):
// a panel outline with an inner rail whose width animates between open/closed.
// Flipped horizontally so the rail sits on the right, matching the chatpad.
const ToggleOpener = styled(ClickButton)`
  anchor-name: --toast-panel-toggle;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  color: var(--text-primary);
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
  }
  & svg {
    transform: scaleX(-1);
  }
  & .inner {
    transition: width var(--motion-base) var(--ease-out);
  }
  &[data-collapsed="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="false"][data-preview="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="true"][data-preview="true"] .inner {
    width: 4px;
  }
  &[data-floating="true"] {
    width: 32px;
    height: 32px;
    background: var(--surface-primary);
    outline: 0.5px solid var(--border-alpha);
    box-shadow: var(--elevation-low);
  }
`;

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  floating?: boolean;
};

export function ChatpadToggleButton({ isOpen, onToggle, floating }: Props) {
  const [hovered, setHovered] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const label = isOpen ? "Hide panel" : "Show panel";
  const toggle = (
    <Tooltip
      vertical="below"
      content={
        <>
          {label} <ShortcutHint>⌘⇧\</ShortcutHint>
        </>
      }
    >
      <ToggleOpener
        aria-label={label}
        aria-keyshortcuts={"Meta+Shift+\\ Control+Shift+\\"}
        aria-expanded={isOpen}
        data-floating={floating}
        data-collapsed={!isOpen}
        data-preview={hovered && !suppressed}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setSuppressed(false);
        }}
        onClick={() => {
          setSuppressed(true);
          onToggle();
        }}
      >
        <PanelToggleIcon />
      </ToggleOpener>
    </Tooltip>
  );
  return floating ? (
    <FloatingToggle data-expanded={isOpen}>{toggle}</FloatingToggle>
  ) : (
    toggle
  );
}
